<script setup lang="ts">
import { productSchema } from '~/utils/crystallize/product'

type RelatedProducts = NonNullable<CrystallizeProduct['components']['relatedProducts']>

const props = defineProps<{
  title?: string
  self: CrystallizeProduct
  data: RelatedProducts
}>()

const { data, suspense } = useProductCatalogue()

await suspense()

const queries = useQueries({
  queries: props.data.map(item => useCatalogueQueryOptions({ path: item.path, schema: productSchema })),
})

const other = computed(() => data?.value?.products?.filter(
  p => !queries.value.some(r => r.data?.id === p.id) && p.id !== props.self.id,
))

const related = computed(() => queries.value.map(it => it.data).filter(it => !!it))

const isHighlighted = computed(() => data?.value?.highlighted?.id === props.self.id)

const highlightedCard = useProductCard(computed(() => data?.value?.highlighted))

const cheapest = computed(() => [...(other.value ?? []), ...related.value].reduce((acc, product) => {
  const variant = getDefaultVariant(product)
  if (!variant) {
    return acc
  }

  return Math.min(acc, getNumericPrice({ price: variant }))
}, Infinity))
</script>

<template>
  <div>
    <ContentGrid>
      <div class="col-span-4 lg:col-span-6 2xl:col-start-2 2xl:col-span-8 flex flex-col gap-m mb-xl">
        <h3 v-text="isHighlighted ? 'Andre abonnementer fra Juridika' : ` Relaterte abonnementer til ${title}`" />
        <p class="title-m !font-light empty:hidden">
          <template v-if="isHighlighted">
            Velg {{ title }} med alt innhold inkludert, eller sett sammen deres eget abonnement med rettsområdene som er mest relevante for dere
            <template v-if="cheapest !== Infinity">
              fra <Price class="title-m !font-light" :price="cheapest" interval="yearly" />
            </template>
          </template>
          <template v-else-if="related?.length">
            Sett sammen deres eget abonnement med rettsområdene som er mest relevante for dere.
          </template>
          <template v-else>
            Få tilgang til alle rettsområder og innhold med <strong class="font-semibold" v-text="highlightedCard?.title" />:
          </template>
        </p>
      </div>
    </ContentGrid>
    <ContentGrid is="ul" class="gap-l">
      <li v-for="(product, i) in (isHighlighted ? other : related)" :key="product.id" class="col-span-4 2xl:col-span-5" :class="{ '2xl:col-start-2': i % 2 === 0 }">
        <NettbutikkProductLinkCard :product="product" class="h-full" />
      </li>
      <li class="full-width-content">
        <template v-if="data?.highlighted && data.highlighted.id !== self.id">
          <p v-if="related?.length" class="mb-l title-m !font-light">
            Eller få tilgang til alle rettsområder og innhold med <strong class="font-semibold" v-text="highlightedCard?.title" />:
          </p>
          <NettbutikkProductCta :product="data.highlighted" size="small" />
        </template>
        <LinkButton v-else :to="{ name: 'product-catalogue' }" size="medium" variant="primary">
          Se&nbsp;alle&nbsp;abonnementer
        </LinkButton>
      </li>
    </ContentGrid>
  </div>
</template>
